import { XIcon } from '@phosphor-icons/react';
import * as SheetPrimitive from '@radix-ui/react-dialog';
import { type VariantProps, cva } from 'class-variance-authority';
import * as React from 'react';

import { CircleIconButton } from '@/components/ui/circle-icon-button';
import { useEscapeRevertGuard } from '@/lib/keyboard';
import { cn } from '@/lib/utils';

const Sheet = SheetPrimitive.Root;

const SheetTrigger = SheetPrimitive.Trigger;

const SheetClose = SheetPrimitive.Close;

const SheetPortal = SheetPrimitive.Portal;

const SheetOverlay = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Overlay
    className={cn(
      // Black, not `--foreground`: in dark mode `--foreground` is 96%
      // lightness, so a foreground-derived scrim is a WHITE wash that makes
      // the page behind brighter than the panel in front. Kept identical to
      // `dialog.tsx` because DialogContent falls back to this Sheet on mobile
      // — a mismatch here would change the scrim purely by viewport width.
      // eslint-disable-next-line no-restricted-syntax
      'fixed inset-0 z-50 bg-black/40',
      // Matched to the content's 350/250 so the scrim and the panel never
      // finish at different times.
      'motion-safe:data-[state=open]:animate-in motion-safe:data-[state=closed]:animate-out',
      'motion-safe:data-[state=open]:fade-in-0 motion-safe:data-[state=closed]:fade-out-0 motion-safe:ease-fluid',
      'motion-safe:data-[state=open]:duration-350 motion-safe:data-[state=closed]:duration-250',
      className,
    )}
    {...props}
    ref={ref}
  />
));
SheetOverlay.displayName = 'SheetOverlay';

const sheetVariants = cva(
  cn(
    'fixed z-50 gap-4 bg-background border border-border/60 p-6 shadow-elevation-3 overflow-hidden',
    // Enter/exit. Radix drives data-[state], tw-animate-css supplies the keyframes.
    // Exit is faster than enter (250/350) — dismissal should feel immediate while
    // arrival feels deliberate.
    'motion-safe:data-[state=open]:animate-in motion-safe:data-[state=closed]:animate-out',
    'motion-safe:data-[state=open]:duration-350 motion-safe:data-[state=closed]:duration-250',
    'motion-safe:ease-fluid',
  ),
  {
    variants: {
      side: {
        top: cn(
          'inset-x-0 top-0 rounded-b-[var(--radius-drawer)]',
          'motion-safe:data-[state=open]:slide-in-from-top motion-safe:data-[state=closed]:slide-out-to-top',
        ),
        bottom: cn(
          'inset-x-0 bottom-0 rounded-t-[var(--radius-drawer)]',
          'motion-safe:data-[state=open]:slide-in-from-bottom motion-safe:data-[state=closed]:slide-out-to-bottom',
        ),
        left: cn(
          'top-0 bottom-0 left-0 h-full w-3/4 rounded-r-[var(--radius-drawer)] sm:max-w-sm',
          'motion-safe:data-[state=open]:slide-in-from-left motion-safe:data-[state=closed]:slide-out-to-left',
        ),
        right: cn(
          'top-0 bottom-0 right-0 h-full w-3/4 rounded-tl-[var(--radius-drawer)] rounded-bl-[var(--radius-drawer)] sm:max-w-sm',
          'motion-safe:data-[state=open]:slide-in-from-right motion-safe:data-[state=closed]:slide-out-to-right',
        ),
      },
    },
    defaultVariants: {
      side: 'right',
    },
  },
);

interface SheetContentProps
  extends
    React.ComponentPropsWithoutRef<typeof SheetPrimitive.Content>,
    VariantProps<typeof sheetVariants> {
  hideCloseButton?: boolean; // Add prop to hide the built-in close button
  overlayClassName?: string;
  /**
   * Mount a nested Sheet inside its owning modal content so the parent
   * dismissable layer treats Sheet interactions as internal.
   */
  portalContainer?: HTMLElement | null;
  /**
   * When true, skip the modal overlay so the page underneath stays interactive.
   * Pair with `<Sheet modal={false}>` on the root to allow clicks through to the
   * page (e.g. PD-2.1 D14: panel + still-clickable calendar).
   */
  noOverlay?: boolean;
}

const SheetContent = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Content>,
  SheetContentProps
>(
  (
    {
      side = 'right',
      className,
      children,
      hideCloseButton = false,
      overlayClassName,
      noOverlay = false,
      portalContainer,
      onEscapeKeyDown,
      ...props
    },
    ref,
  ) => {
    const contentRef = React.useRef<HTMLDivElement>(null);
    const handleEscapeKeyDown = useEscapeRevertGuard(onEscapeKeyDown);

    return (
      <SheetPortal container={portalContainer ?? undefined}>
        {!noOverlay && <SheetOverlay className={overlayClassName} />}
        <SheetPrimitive.Content
          ref={(node) => {
            // Handle both refs
            if (ref) {
              if (typeof ref === 'function') ref(node);
              else ref.current = node;
            }
            contentRef.current = node;
          }}
          className={cn(sheetVariants({ side }), className)}
          onEscapeKeyDown={handleEscapeKeyDown}
          {...props}
        >
          {children}
          {!hideCloseButton && (
            <SheetPrimitive.Close asChild>
              <CircleIconButton
                ariaLabel="Close"
                size="sm"
                className="absolute right-4 top-4 opacity-70 transition-colors hover:bg-muted hover:opacity-100 data-[state=open]:bg-secondary"
              >
                <XIcon className="size-4" weight="bold" />
              </CircleIconButton>
            </SheetPrimitive.Close>
          )}
        </SheetPrimitive.Content>
      </SheetPortal>
    );
  },
);
SheetContent.displayName = 'SheetContent';

const SheetHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>): JSX.Element => (
  <div className={cn('flex flex-col space-y-2 text-center sm:text-left', className)} {...props} />
);
SheetHeader.displayName = 'SheetHeader';

const SheetFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>): JSX.Element => (
  <div
    className={cn('flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2', className)}
    {...props}
  />
);
SheetFooter.displayName = 'SheetFooter';

/**
 * MATCHES `DialogTitle` EXACTLY — 16px / 500 / tracking-tight.
 *
 * This was `font-semibold` with normal tracking, i.e. the identical defect that
 * took six rounds of founder feedback on `DialogTitle` before anyone measured
 * it: heavier AND missing the tight tracking, two compounding differences that
 * are invisible one at a time and read as "way too bold" together. `dialog.tsx`
 * was corrected on 2026-08-12; this sibling primitive was left behind and kept
 * shouting, which the founder caught on the Concept brief panel 2026-08-15.
 *
 * A sheet header and a dialog header are the same object at different anchors.
 * If one changes, change both — and per the design system, do NOT restate these
 * values at call sites, or a future fix here reaches one surface out of sixteen.
 */
const SheetTitle = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Title>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Title
    ref={ref}
    className={cn('text-base font-medium tracking-tight text-foreground', className)}
    {...props}
  />
));
SheetTitle.displayName = 'SheetTitle';

const SheetDescription = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Description>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Description
    ref={ref}
    className={cn('text-sm text-muted-foreground', className)}
    {...props}
  />
));
SheetDescription.displayName = 'SheetDescription';

export {
  Sheet,
  SheetPortal,
  SheetOverlay,
  SheetTrigger,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetFooter,
  SheetTitle,
  SheetDescription,
};
